<template>
  <validation-observer ref="observer">
    <b-card v-if="availableServices.length > 0" class="mb-2">
      <template #header>
        <div class="d-flex align-items-center justify-content-between">
          <h4 class="mb-0">{{ $t('Additional Services') }}</h4>
          <feather-icon icon="ChevronRightIcon" size="18" />
        </div>
      </template>

      <div class="mb-2">
        <p class="text-muted small mb-2">
          {{
            $t(
              'You can choose additional services for your shipment. Multiple services can be selected.'
            )
          }}
        </p>

        <div
          v-for="service in displayedServices"
          :key="service.code"
          class="mb-1 border rounded p-1 cursor-pointer"
          :class="{ 'border-primary': isSelected(service.code) }"
        >
          <div class="d-flex align-items-center mb-1">
            <b-form-checkbox
              :checked="isSelected(service.code)"
              class="mr-1"
              @change="toggleService(service.code)"
            />
            <div class="d-flex align-items-center flex-grow-1" @click="toggleService(service.code)">
              <div
                class="d-flex align-items-center justify-content-center bg-light-primary rounded mr-1"
                style="width: 40px; height: 40px"
              >
                <feather-icon :icon="service.icon" size="20" class="text-primary" />
              </div>
              <div>
                <h6 class="mb-0">{{ $t(service.titleKey) }}</h6>
                <small class="text-muted">{{ $t(service.descriptionKey) }}</small>
              </div>
            </div>
          </div>

          <!-- Service Details Inputs -->
          <div v-if="isSelected(service.code) && service.hasInputs" class="pl-3 mt-1">
            <b-row>
              <!-- Cash On Delivery Inputs -->
              <template v-if="service.code === 'CASH_ON_DELIVERY'">
                <b-col md="6">
                  <validation-provider #default="{ errors }" :name="$t('fee')" rules="required">
                    <b-form-group :label="$t('fee')">
                      <b-form-input
                        v-model.number="serviceModels[service.code].amount"
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        :class="errors.length > 0 ? 'is-invalid' : ''"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </b-col>
                <b-col md="6">
                  <validation-provider
                    #default="{ errors }"
                    :name="$t('Postal Check Account')"
                    rules="required"
                  >
                    <b-form-group :label="$t('Postal Check Account')">
                      <b-form-input
                        v-model="serviceModels[service.code].accountNumber"
                        :placeholder="$t('Enter account number')"
                        :class="errors.length > 0 ? 'is-invalid' : ''"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </b-col>
              </template>

              <!-- Valued Shipment Inputs -->
              <template v-if="service.code === 'VALUED_SHIPMENT'">
                <b-col md="12">
                  <validation-provider
                    #default="{ errors }"
                    :name="$t('Product Value (TL)')"
                    rules="required"
                  >
                    <b-form-group :label="$t('Product Value (TL)')">
                      <b-form-input
                        v-model.number="serviceModels[service.code].value"
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        :class="errors.length > 0 ? 'is-invalid' : ''"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </b-col>
              </template>
            </b-row>
          </div>
        </div>
      </div>
    </b-card>
  </validation-observer>
</template>

<script>
import { BCard, BFormCheckbox, BRow, BCol, BFormGroup, BFormInput } from 'bootstrap-vue';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import useService from '@/auth/service/useService';

export default {
  name: 'ConsignmentSpecialServices',
  components: {
    BCard,
    BFormCheckbox,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    ValidationProvider,
    ValidationObserver
  },
  props: {
    providerId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      availableServices: [],
      serviceModels: {
        CASH_ON_DELIVERY: {
          method: 'CASH',
          amount: null,
          accountNumber: ''
        },
        VALUED_SHIPMENT: {
          value: null
        }
      },
      serviceDefinitions: {
        CASH_ON_DELIVERY: {
          code: 'CASH_ON_DELIVERY',
          titleKey: 'Cash on Delivery',
          descriptionKey: 'Collect product price at the door',
          icon: 'CreditCardIcon',
          hasInputs: true
        },
        SHIPPING_FEE_ON_DELIVERY: {
          code: 'SHIPPING_FEE_ON_DELIVERY',
          titleKey: 'Shipping Fee on Delivery',
          descriptionKey: 'Collect shipping fee from receiver',
          icon: 'PackageIcon',
          hasInputs: false
        },
        HOLD_AT_DEPARTMENT: {
          code: 'HOLD_AT_DEPARTMENT',
          titleKey: 'Hold at Department',
          descriptionKey: 'Hold shipment at branch for pickup',
          icon: 'HomeIcon',
          hasInputs: false
        },
        VALUED_SHIPMENT: {
          code: 'VALUED_SHIPMENT',
          titleKey: 'Valued Shipment',
          descriptionKey: 'Extra security for high value shipments',
          icon: 'ShieldIcon',
          hasInputs: true
        },
        CONTROLLED_DELIVERY: {
          code: 'CONTROLLED_DELIVERY',
          titleKey: 'Controlled Delivery',
          descriptionKey: 'Extra control during delivery process',
          icon: 'CheckCircleIcon',
          hasInputs: false
        }
      },
      selectedServices: []
    };
  },
  computed: {
    displayedServices() {
      return this.availableServices.map(code => this.serviceDefinitions[code]).filter(Boolean);
    }
  },
  watch: {
    providerId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.fetchServices();
        } else {
          this.availableServices = [];
          this.selectedServices = [];
        }
      }
    },
    selectedServices: {
      deep: true,
      handler() {
        this.emitUpdates();
      }
    },
    serviceModels: {
      deep: true,
      handler() {
        this.emitUpdates();
      }
    }
  },
  methods: {
    async validate() {
      return this.$refs.observer.validate();
    },
    isSelected(code) {
      return this.selectedServices.includes(code);
    },
    toggleService(code) {
      const index = this.selectedServices.indexOf(code);
      if (index === -1) {
        this.selectedServices.push(code);
      } else {
        this.selectedServices.splice(index, 1);
      }
    },
    async fetchServices() {
      try {
        const response = await useService.getSpecialServicesConsignmentsCreate(this.providerId);
        if (response.status && response.data) {
          this.availableServices = response.data;
        } else {
          this.availableServices = [];
        }
      } catch (error) {
        this.availableServices = [];
      }
    },
    emitUpdates() {
      const payload = this.selectedServices.map(code => {
        const service = { service_type: code };
        if (this.serviceDefinitions[code].hasInputs) {
          service.details = { ...this.serviceModels[code] };
        }
        return service;
      });
      this.$emit('input', payload);
    }
  }
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
